<template>
  <v-dialog 
    v-model="localDialog" 
    max-width="1000px"
    width="95vw"
    max-height="90vh"
    persistent
    scroll-strategy="block"
    retain-focus
    fullscreen-on-mobile
    scrollable
    class="qr-template-dialog"
  >
    <v-card class="template-selector-card d-flex flex-column" style="height: 100%;">
      <v-card-title class="d-flex align-center justify-space-between bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-4 md:px-6 py-3 md:py-4 flex-shrink-0">
        <div class="d-flex align-center">
          <v-icon class="mr-2 md:mr-3" :size="$vuetify.display.mobile ? 24 : 28">mdi-qrcode</v-icon>
          <span class="text-lg md:text-xl font-bold">Choose QR Template</span>
        </div>
        <v-btn 
          icon 
          variant="text" 
          @click="closeDialog"
          class="text-white"
          :size="$vuetify.display.mobile ? 'small' : 'default'"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-4 md:pa-6 flex-grow-1 overflow-y-auto">
        <p class="text-gray-600 mb-4 md:mb-6 text-center text-sm md:text-base">
          Select a template design for your QR codes. Each template offers different levels of information and styling.
        </p>

        <div class="template-grid">
          <!-- Standard QR Layout Template -->
          <div 
            class="template-option"
            :class="{ 'selected': selectedTemplate === 'StandardQRLayout' }"
            @click="selectTemplate('StandardQRLayout')"
          >
            <div class="template-preview">
              <img 
                src="/TemplateImages/StandardQRLayout.webp" 
                alt="Standard QR Layout Template Preview"
                class="template-image"
                loading="lazy"
              />
            </div>
            
            <div class="template-label">
              <v-icon class="mr-1 md:mr-2" :size="$vuetify.display.mobile ? 16 : 20" color="primary">mdi-card-text-outline</v-icon>
              <span class="template-text">Standard Layout</span>
            </div>
          </div>

          <!-- Modern Template -->
          <div 
            class="template-option"
            :class="{ 'selected': selectedTemplate === 'ModernTemplate' }"
            @click="selectTemplate('ModernTemplate')"
          >
            <div class="template-preview">
              <img 
                src="/TemplateImages/ModernTemplate.webp" 
                alt="Modern Template Preview"
                class="template-image"
                loading="lazy"
              />
            </div>
            
            <div class="template-label">
              <v-icon class="mr-1 md:mr-2" :size="$vuetify.display.mobile ? 16 : 20" color="purple">mdi-palette-outline</v-icon>
              <span class="template-text">Cruise Themed</span>
            </div>
          </div>

          <!-- Just QR Code Template -->
          <div 
            class="template-option"
            :class="{ 'selected': selectedTemplate === 'JustQRCode' }"
            @click="selectTemplate('JustQRCode')"
          >
            <div class="template-preview">
              <img 
                src="/TemplateImages/JustQRCode.webp" 
                alt="Just QR Code Template Preview"
                class="template-image"
                loading="lazy"
              />
            </div>
            
            <div class="template-label">
              <v-icon class="mr-1 md:mr-2" :size="$vuetify.display.mobile ? 16 : 20" color="grey-darken-2">mdi-qrcode-scan</v-icon>
              <span class="template-text">Just QR Code</span>
            </div>
          </div>
        </div>
      </v-card-text>

      <v-card-actions class="px-4 md:px-6 pb-4 md:pb-6 flex-column flex-sm-row gap-2 flex-shrink-0 border-t border-gray-200">
        <v-btn 
          variant="outlined" 
          @click="closeDialog"
          class="w-full w-sm-auto order-2 order-sm-1"
          :size="$vuetify.display.mobile ? 'large' : 'default'"
        >
          Cancel
        </v-btn>
        <v-spacer class="d-none d-sm-block" />
        <v-btn 
          color="primary" 
          :disabled="!selectedTemplate || loading"
          :loading="loading"
          @click="generateQRCodes"
          class="w-full w-sm-auto order-1 order-sm-2"
          :size="$vuetify.display.mobile ? 'large' : 'default'"
        >
          <v-icon class="mr-2">mdi-download</v-icon>
          <span class="d-none d-sm-inline">Generate QR Codes</span>
          <span class="d-sm-none">Generate</span>
        </v-btn>
      </v-card-actions>
    </v-card>
    
    <!-- QR Download Loading Screen -->
    <QRDownloadLoadingScreen
      :model-value="loading"
      type="bulk"
      :template-name="getTemplateDisplayName(selectedTemplate)"
      :duck-count="duckCount"
      :cruise-name="cruiseName"
    />
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import QRDownloadLoadingScreen from '@/components/ui/QRDownloadLoadingScreen.vue';

// Props
interface Props {
  modelValue: boolean;
  cruiseName?: string;
  loading?: boolean;
  duckCount?: number;
}

const props = withDefaults(defineProps<Props>(), {
  cruiseName: '',
  loading: false,
  duckCount: 0
});

// Emits
const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  'template-selected': [template: string];
}>();

// State
const selectedTemplate = ref<'StandardQRLayout' | 'ModernTemplate' | 'JustQRCode' | null>(null);

// Computed
const localDialog = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
});

// Methods
const selectTemplate = (template: 'StandardQRLayout' | 'ModernTemplate' | 'JustQRCode') => {
  selectedTemplate.value = template;
};

const generateQRCodes = () => {
  if (selectedTemplate.value) {
    emit('template-selected', selectedTemplate.value);
  }
};

const closeDialog = () => {
  selectedTemplate.value = null;
  localDialog.value = false;
};

const getTemplateDisplayName = (template: 'StandardQRLayout' | 'ModernTemplate' | 'JustQRCode' | null): string => {
  switch (template) {
    case 'StandardQRLayout':
      return 'Standard QR Layout';
    case 'ModernTemplate':
      return 'Cruise Themed Template';
    case 'JustQRCode':
      return 'Just QR Code';
    default:
      return '';
  }
};
</script>

<style scoped>
.template-selector-card {
  overflow: hidden;
}

/* Template Grid Layout */
.template-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}

@media (min-width: 640px) {
  .template-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }
}

@media (min-width: 768px) {
  .template-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
  }
}

.template-option {
  cursor: pointer;
  border: 3px solid transparent;
  border-radius: 16px;
  transition: all 0.3s ease;
  padding: 12px;
  background: #ffffff;
  /* Mobile touch target size */
  min-height: 44px;
  -webkit-tap-highlight-color: transparent;
}

.template-option:hover {
  border-color: #e3f2fd;
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
}

.template-option:active {
  transform: translateY(-1px);
}

.template-option.selected {
  border-color: #2196f3;
  background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%);
  box-shadow: 0 8px 25px rgba(33, 150, 243, 0.3);
}

/* Template Preview */
.template-preview {
  width: 100%;
  height: 180px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  position: relative;
}

@media (min-width: 640px) {
  .template-preview {
    height: 220px;
    margin-bottom: 16px;
  }
}

@media (min-width: 768px) {
  .template-preview {
    height: 280px;
  }
}

.template-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 12px;
  transition: transform 0.3s ease;
}

.template-option:hover .template-image {
  transform: scale(1.02);
}

/* Template Labels */
.template-label {
  text-align: center;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #e0e0e0;
  min-height: 44px; /* Ensure touch target size */
}

.template-text {
  font-size: 14px;
  line-height: 1.2;
  text-align: center;
}

@media (min-width: 640px) {
  .template-text {
    font-size: 15px;
  }
  
  .template-label {
    padding: 10px 12px;
  }
}

@media (min-width: 768px) {
  .template-text {
    font-size: 16px;
  }
  
  .template-label {
    padding: 12px;
  }
}

/* Mobile-specific improvements */
@media (max-width: 639px) {
  .template-option {
    padding: 16px;
    border-radius: 12px;
  }
  
  .template-option:hover {
    transform: none; /* Disable transform on mobile for better performance */
  }
  
  .template-option:active {
    transform: scale(0.98);
    transition: transform 0.1s ease;
  }
  
  /* Make touch area larger */
  .template-preview {
    margin-bottom: 16px;
  }
  
  /* Ensure dialog takes full height on mobile */
  .template-selector-card {
    height: 100vh !important;
    max-height: 100vh !important;
  }
  
  /* Sticky actions on mobile */
  .v-card-actions {
    position: sticky;
    bottom: 0;
    background: white;
    z-index: 10;
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
  }
}

/* Animations */
@keyframes bounce {
  0%, 20%, 53%, 80%, 100% {
    transform: translateY(0);
  }
  40%, 43% {
    transform: translateY(-8px);
  }
  70% {
    transform: translateY(-4px);
  }
  90% {
    transform: translateY(-2px);
  }
}

.animate-bounce {
  animation: bounce 2s infinite;
}

/* Mobile scroll indicator */
.mobile-scroll-indicator {
  animation: gentle-pulse 2s ease-in-out infinite;
}

@keyframes gentle-pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

/* Loading states */
.template-option.loading {
  pointer-events: none;
  opacity: 0.7;
}

/* Focus states for accessibility */
.template-option:focus-visible {
  outline: 2px solid #2196f3;
  outline-offset: 2px;
}

/* Dark mode support */
.v-theme--dark .template-option {
  background: #1e293b;
  border-color: transparent;
}

.v-theme--dark .template-option:hover {
  border-color: #334155;
}

.v-theme--dark .template-option.selected {
  background: linear-gradient(135deg, #1e40af 0%, #7c3aed 100%);
  border-color: #3b82f6;
}

.v-theme--dark .template-label {
  background: rgba(30, 41, 59, 0.9);
  border-color: #475569;
  color: #e2e8f0;
}

.v-theme--dark .template-preview {
  background: #334155;
}
</style>
